import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axios";
import NewEquipmentModal from "../components/NewEquipmentModal";
import DataTable from "../components/DataTable";
import { API_ENDPOINTS } from "../config/api";

const Dashboard = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState(null);
  const totalPages = 10;
  const hasFetched = useRef(false);

  // Function to convert status number to Turkish text
  const getStatusText = (status) => {
    const statusMap = {
      11: "Arıza",
      22: "Bağlantı Yok",
      31: "Kapalı",
      32: "Pasif",
      33: "Aktif",
    };
    return statusMap[status] || `Status: ${status}`;
  };

  // Function to format location
  const getLocationText = (location) => {
    if (!location || location === null) {
      return "Bilinmeyen";
    }

    // Location is always an object or null
    return formatLocationObject(location);
  };

  // Function to format location object into readable text
  const formatLocationObject = (locationObj) => {
    if (!locationObj) return "Bilinmeyen";

    const parts = [];

    if (locationObj.campus) parts.push(`Kampüs: ${locationObj.campus}`);
    if (locationObj.building) parts.push(`Bina: ${locationObj.building}`);
    if (locationObj.floor) parts.push(`Kat: ${locationObj.floor}`);
    if (locationObj.definition) parts.push(`Tanım: ${locationObj.definition}`);
    if (locationObj.name) parts.push(`Kod: ${locationObj.name}`);

    return parts.length > 0 ? parts.join("  / ") : "Bilinmeyen";
  };

  // Function to format tracking time
  const getTrackingText = (trackingTime) => {
    if (!trackingTime) {
      return "Takip başlamadı";
    }
    // If it's a date string, format it
    if (typeof trackingTime === "string" && trackingTime.includes("T")) {
      const date = new Date(trackingTime);
      return date.toLocaleString("tr-TR");
    }
    return trackingTime;
  };

  // Function to convert category enum to Turkish
  const getCategoryText = (category) => {
    const categoryMap = {
      Other: "Diğer",
      "Power Supply": "Güç Kaynağı",
      Oscilloscope: "Osiloskop",
      "Network Analyzer": "Ağ Analizörü",
      "Signal Analyzer": "Sinyal Analizörü",
      "Spectrum Analyzer": "Spektrum Analizörü",
      "Power Amplifier": "Güç Yükseltici",
      "IFF Test Device": "IFF Test Cihazı",
    };
    return categoryMap[category] || category || "N/A";
  };

  // Function to check available nodes and open modal
  const handleOpenModal = async () => {
    setModalError(null);
    try {
      const response = await axiosInstance.get("/v1/nodes/undefined/count");
      const count = response.data.count;

      if (count === 0) {
        setModalError("Tanımlanacak node yoktur");
        // Auto-hide warning after 3 seconds
        setTimeout(() => {
          setModalError(null);
        }, 3000);
        return;
      }

      setIsModalOpen(true);
    } catch (err) {
      console.error("Error checking node count:", err);
      setModalError("Node sayısı kontrol edilirken hata oluştu");
      // Auto-hide error after 3 seconds
      setTimeout(() => {
        setModalError(null);
      }, 3000);
    }
  };

  // Function to refresh data
  const refreshData = (isPolling = false) => {
    const fetchInventoryData = async () => {
      // Sadece ilk yüklemede loading göster, polling'de gösterme
      if (!isPolling) {
        setLoading(true);
      }
      setError(null);
      try {
        const response = await axiosInstance.get(API_ENDPOINTS.GET_NODE_INVENTORY);
        console.log("Inventory data received:", response.data);

        // Transform backend data to match table structure
        const transformedData = response.data.map((node) => ({
          node: node.deviceId || "",
          category: getCategoryText(node.investmentInventoryCategory),
          assetNo: node.fixedAssetNumber || "",
          tagNo: node.tagNumber || "",
          brandModel: node.brand && node.model ? `${node.brand} / ${node.model}` : node.brand || node.model || "",
          acquisitionYear: node.yearOfInventory || "",
          trackingStartTime: getTrackingText(node.trackingStartTime),
          totalOperatingTime: node.totalOperatingTimeFormatted || "0 dk",
          location: getLocationText(node.location),
          status: getStatusText(node.status),
        }));

        setDashboardData(transformedData);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "Veri yüklenirken hata oluştu";
        setError(errorMessage);
        console.error("Error fetching inventory data:", err);
        if (err.response) {
          console.error("Response data:", err.response.data);
          console.error("Response status:", err.response.status);
        }
      } finally {
        // Sadece ilk yüklemede loading'i kapat, polling'de kapatma
        if (!isPolling) {
          setLoading(false);
        }
      }
    };

    fetchInventoryData();
  };

  // Fetch inventory data from API on component mount
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      refreshData();
    }
  }, []);

  // Polling useEffect - Her 5 saniyede bir veri güncelle
  useEffect(() => {
    let interval;
    let isPageVisible = true;

    const startPolling = () => {
      if (interval) return;

      console.log("Polling başlatıldı...");
      interval = setInterval(() => {
        if (isPageVisible && !document.hidden) {
          console.log("Veri güncelleniyor...");
          refreshData(true); // Polling olduğunu belirt
        }
      }, 5000); // Her 5 saniyede bir
    };

    const stopPolling = () => {
      if (interval) {
        console.log("Polling durduruldu...");
        clearInterval(interval);
        interval = null;
      }
    };

    // Sayfa görünürlük değişikliği
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isPageVisible = false;
        stopPolling();
      } else {
        isPageVisible = true;
        startPolling();
      }
    };

    // Event listener'ı ekle
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // İlk başlatma
    startPolling();

    // Cleanup
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const columns = [
    { key: "node", header: "Node" },
    { key: "category", header: "Yatırım Envanter Kategorisi" },
    { key: "assetNo", header: "Duran Varlık No" },
    { key: "tagNo", header: "Etiket No" },
    { key: "brandModel", header: "Marka / Model" },
    { key: "acquisitionYear", header: "Envantere Alım Yılı" },
    { key: "trackingStartTime", header: "Takip Başlangıç Tarihi" },
    { key: "totalOperatingTime", header: "Toplam Çalışma Süresi (dk)" },
    { key: "location", header: "Konum" },
    { key: "status", header: "Durum" },
    { key: "edit", header: "Detay" },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
        <div className="flex items-center space-x-4">
          <button onClick={handleOpenModal} className="text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors hover:opacity-90" style={{ backgroundColor: "#1570EF" }}>
            <span>+</span>
            <span>Yeni Node Tanımla</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">{error}</div>}

      {/* Modal Error Message - Top Right */}
      {modalError && (
        <div
          className="fixed z-[9999] w-80 h-16 p-4 bg-white text-gray-800 rounded-lg shadow-lg relative overflow-hidden animate-slide-in-right"
          style={{
            position: "fixed",
            top: "16px",
            right: "16px",
            left: "auto",
            transform: "none",
          }}
        >
          <div className="flex items-center h-full">
            <div className="w-2 h-2 bg-red-400 rounded-full mr-3"></div>
            <span className="text-sm font-medium">{modalError}</span>
          </div>
          {/* Animated progress bar */}
          <div className="absolute bottom-0 left-0 h-1 bg-red-400 animate-progress"></div>
        </div>
      )}

      {/* Data Table */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-600">Veriler yükleniyor...</div>
        </div>
      ) : dashboardData.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="text-gray-500 text-lg mb-2">📋</div>
            <div className="text-gray-600 text-lg">Veri bulunamadı</div>
            <div className="text-gray-400 text-sm mt-1">Henüz hiç node tanımlanmamış</div>
          </div>
        </div>
      ) : (
        <DataTable
          data={dashboardData}
          columns={columns}
          // currentPage={currentPage}
          // totalPages={totalPages}
          // onPageChange={setCurrentPage}
          onEdit={(row) => navigate(`/power-source/${row.node}`)}
        />
      )}

      {/* Modal */}
      <NewEquipmentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setModalError(null); // Clear modal error
          hasFetched.current = false; // Reset flag to allow refresh
          refreshData(); // Refresh data when modal closes
        }}
      />
    </div>
  );
};

export default Dashboard;
